import { Component, ErrorInfo, ReactNode } from 'react'
import { RefreshCw } from 'lucide-react'
import { Button } from '@/design-system/primitives'
import { Broke, Working } from '@/design-system/feedback'
import '@/design-system/tokens.css'
import '@/design-system/components.css'
import '@/design-system/feedback.css'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  isChunkError: boolean
}

// Session storage key for tracking reload attempts
const RELOAD_ATTEMPTS_KEY = 'chunk_error_reload_attempts'
const MAX_RELOAD_ATTEMPTS = 3
const RELOAD_ATTEMPT_RESET_MS = 30000 // Reset counter after 30 seconds of stability

// Flag to prevent double reloads from both boundary and global handler
let isReloading = false

export function isChunkLoadError(error: Error): boolean {
  const message = error.message || ''
  const name = error.name || ''

  return (
    name === 'ChunkLoadError' ||
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Unable to preload CSS')
  )
}

export function triggerChunkErrorReload(): boolean {
  // Prevent double reloads
  if (isReloading) {
    return false
  }

  // Check reload attempts to prevent infinite loops
  const attemptsData = sessionStorage.getItem(RELOAD_ATTEMPTS_KEY)
  let attempts = 0

  if (attemptsData) {
    try {
      const parsed = JSON.parse(attemptsData)
      const timeSinceLastAttempt = Date.now() - (parsed.timestamp || 0)

      // Reset counter if enough time has passed (app was stable)
      if (timeSinceLastAttempt > RELOAD_ATTEMPT_RESET_MS) {
        attempts = 0
      } else {
        attempts = parsed.count || 0
      }
    } catch {
      attempts = 0
    }
  }

  if (attempts >= MAX_RELOAD_ATTEMPTS) {
    console.error('Max chunk error reload attempts reached. Please refresh manually.')
    return false
  }

  // Increment and store attempt count
  sessionStorage.setItem(RELOAD_ATTEMPTS_KEY, JSON.stringify({
    count: attempts + 1,
    timestamp: Date.now()
  }))

  isReloading = true
  return true
}

class ChunkErrorBoundary extends Component<Props, State> {
  private reloadTimeoutId: ReturnType<typeof setTimeout> | null = null

  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, isChunkError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    const isChunkError = isChunkLoadError(error)
    return { hasError: true, isChunkError }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error boundary caught:', error, errorInfo)

    // If it's a chunk error, auto-reload after a brief delay
    if (this.state.isChunkError) {
      const shouldReload = triggerChunkErrorReload()

      if (shouldReload) {
        this.reloadTimeoutId = setTimeout(() => {
          window.location.reload()
        }, 100)
      }
    }
  }

  componentWillUnmount() {
    // Clean up timeout to prevent memory leaks
    if (this.reloadTimeoutId) {
      clearTimeout(this.reloadTimeoutId)
      this.reloadTimeoutId = null
    }
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        // Check if we've exceeded max attempts
        const attemptsData = sessionStorage.getItem(RELOAD_ATTEMPTS_KEY)
        let attempts = 0
        if (attemptsData) {
          try {
            attempts = JSON.parse(attemptsData).count || 0
          } catch {
            attempts = 0
          }
        }

        /* The three screens below are the system's own: `Broke` says what
           went wrong in words and offers one thing to do, `Working` says what
           is happening. A bare `.ds` scope follows the document's theme, and
           `ds-boot` centres it on the whole window, because when this shows
           there is nothing else on the page. */
        if (attempts >= MAX_RELOAD_ATTEMPTS) {
          // Show manual refresh UI if auto-reload failed multiple times
          return (
            <div className="ds ds-boot">
              <div className="ds-boot__in ds-boot__in--words">
                <Broke
                  title="SuperSchema has updated"
                  detail="The version in this tab is out of date, and it could not refresh itself. Reload to get the current one."
                  secondary={
                    <Button onClick={this.handleReload}>
                      <RefreshCw aria-hidden="true" />
                      Reload
                    </Button>
                  }
                />
              </div>
            </div>
          )
        }

        return (
          <div className="ds ds-boot">
            <div className="ds-boot__in ds-boot__in--words">
              <Working
                title="Getting the latest version"
                note="SuperSchema has updated since this page loaded. It will reload itself in a moment."
              />
            </div>
          </div>
        )
      }

      // Generic error fallback
      return (
        <div className="ds ds-boot">
          <div className="ds-boot__in ds-boot__in--words">
            <Broke
              title="Something went wrong on this page"
              detail="Reloading usually clears it. If it keeps happening, tell us and we will look."
              secondary={
                <Button onClick={this.handleReload}>
                  <RefreshCw aria-hidden="true" />
                  Reload
                </Button>
              }
            />
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ChunkErrorBoundary
